import { useEffect, useState } from "react";
import { Sidebar, SidebarContent } from "@/components/Sidebar";
import { BauenLogo } from "@/components/BauenLogo";
import { Outlet, useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Users, FolderKanban, FileText, Menu, Search as SearchIcon } from "lucide-react";

export function AppLayout() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ clients: [], projects: [], documents: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults({ clients: [], projects: [], documents: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch (_e) { void _e; }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setSearchOpen(false); setQ(""); navigate(path); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <Sidebar onSearchOpen={() => setSearchOpen(true)} />

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-b h-14 flex items-center justify-between px-3">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" aria-label="Abrir menu">
              <Menu size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-sidebar">
            <SidebarContent onSearchOpen={() => { setMobileNavOpen(false); setSearchOpen(true); }} onNavigate={() => setMobileNavOpen(false)} />
          </SheetContent>
        </Sheet>
        <BauenLogo className="h-9" />
        <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} data-testid="mobile-search-btn" aria-label="Pesquisar">
          <SearchIcon size={18} />
        </Button>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0" data-testid="main-content">
        <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput placeholder="Pesquisar clientes, projetos ou documentos…" value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>Nenhum resultado.</CommandEmpty>
          {results.clients?.length > 0 && (
            <CommandGroup heading="Clientes">
              {results.clients.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/clients/${c.id}`)} data-testid={`search-client-${c.id}`}>
                  <Users className="mr-2 h-4 w-4" /> <span className="flex-1">{c.name}</span>
                  {c.company && <span className="text-xs text-muted-foreground">{c.company}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.projects?.length > 0 && (
            <CommandGroup heading="Projetos">
              {results.projects.map((p) => (
                <CommandItem key={p.id} onSelect={() => go(`/projects/${p.id}`)} data-testid={`search-project-${p.id}`}>
                  <FolderKanban className="mr-2 h-4 w-4" /> <span className="flex-1">{p.name}</span>
                  {p.code && <span className="text-xs text-muted-foreground font-mono">{p.code}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.documents?.length > 0 && (
            <CommandGroup heading="Documentos">
              {results.documents.map((d) => (
                <CommandItem key={d.id} onSelect={() => go(`/projects/${d.project_id}`)} data-testid={`search-document-${d.id}`}>
                  <FileText className="mr-2 h-4 w-4" /> <span className="flex-1">{d.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
